import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import type {
  CopilotPromptConfigurationsDto,
  ILoqProjectConfigurationDto,
  RemsProjectConfigurationDto,
} from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Notice } from "@/design-system/Notice";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { CopilotConfigurationsForm } from "@/modules/project/pages/integrations/pages/CopilotPage";
import { ILoqForm } from "@/modules/project/pages/integrations/pages/ILoqPage";
import { RemsForm } from "@/modules/project/pages/integrations/pages/RemsPage";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { useTranslation } from "@/translations";

import type { PortfolioIntegrationConfiguration, PortfolioIntegrationProject } from "./types";

type IntegrationProjectSetupQueueProps<TConfiguration extends PortfolioIntegrationConfiguration> = {
  rows: PortfolioIntegrationProject<TConfiguration>[];
  selectedProjectIds: Set<string>;
};

type ProjectSetupQueueProps<TConfiguration extends PortfolioIntegrationConfiguration> =
  IntegrationProjectSetupQueueProps<TConfiguration> & {
    getNextProject: (
      remainingQueue: PortfolioIntegrationProject<TConfiguration>[],
    ) => PortfolioIntegrationProject<TConfiguration> | undefined;
    renderProjectSetup: (projectId: string, onSaved: () => void) => React.ReactNode;
  };

export function AiProjectSetupQueue(
  props: IntegrationProjectSetupQueueProps<CopilotPromptConfigurationsDto>,
): React.ReactNode {
  return (
    <ProjectSetupQueue
      {...props}
      getNextProject={(remainingQueue) => remainingQueue[0]}
      renderProjectSetup={(projectId, onSaved) => (
        <AiProjectSetup key={projectId} projectId={projectId} onSaved={onSaved} />
      )}
    />
  );
}

export function RemsProjectSetupQueue(
  props: IntegrationProjectSetupQueueProps<RemsProjectConfigurationDto>,
): React.ReactNode {
  return (
    <ProjectSetupQueue
      {...props}
      getNextProject={(remainingQueue) => remainingQueue.find((row) => row.status === "setupIncomplete")}
      renderProjectSetup={(projectId, onSaved) => (
        <RemsProjectSetup key={projectId} projectId={projectId} onSaved={onSaved} />
      )}
    />
  );
}

export function ILoqProjectSetupQueue(
  props: IntegrationProjectSetupQueueProps<ILoqProjectConfigurationDto>,
): React.ReactNode {
  return (
    <ProjectSetupQueue
      {...props}
      getNextProject={(remainingQueue) => remainingQueue.find((row) => row.status === "setupIncomplete")}
      renderProjectSetup={(projectId, onSaved) => (
        <ILoqProjectSetup key={projectId} projectId={projectId} onSaved={onSaved} />
      )}
    />
  );
}

function ProjectSetupQueue<TConfiguration extends PortfolioIntegrationConfiguration>({
  rows,
  selectedProjectIds,
  getNextProject,
  renderProjectSetup,
}: ProjectSetupQueueProps<TConfiguration>): React.ReactNode {
  const { t } = useTranslation();
  const queue = useMemo(
    () => rows.filter((row) => row.canManage && selectedProjectIds.has(row.project.id)),
    [rows, selectedProjectIds],
  );
  const [requestedProjectId, setRequestedProjectId] = useState<string | undefined>();

  if (queue.length === 0) {
    return <Notice variant="special" message={t("page.portfolio-integrations.project-setup.empty")} />;
  }

  const activeProjectId = queue.some((row) => row.project.id === requestedProjectId)
    ? requestedProjectId
    : (queue.find((row) => row.status === "setupIncomplete")?.project.id ?? queue[0].project.id);
  const activeIndex = queue.findIndex((row) => row.project.id === activeProjectId);
  const activeRow = queue[activeIndex] ?? queue[0];
  const openNext = () => {
    const remainingQueue = [...queue.slice(activeIndex + 1), ...queue.slice(0, activeIndex)];
    const next = getNextProject(remainingQueue);
    setRequestedProjectId(next?.project.id);
  };

  return (
    <Stack gap="md">
      <FormField label={t("page.portfolio-integrations.projects.project")}>
        <Select
          width="compact"
          items={queue}
          selected={activeRow}
          keySelector={(row) => row.project.id}
          renderOption={(row) => row.project.name}
          onChange={(row) => setRequestedProjectId(row.project.id)}
        />
      </FormField>
      <section className="min-w-0">{renderProjectSetup(activeRow.project.id, openNext)}</section>
    </Stack>
  );
}

function AiProjectSetup({ projectId, onSaved }: { projectId: string; onSaved: () => void }): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const integrationQueries = useIntegrationQueries();
  const configQuery = useQuery(integrationQueries.copilotConfigurations(projectId));
  const mutation = integrationMutations.useEditCopilotConfigurations(projectId);

  if (configQuery.error) {
    return <ErrorPage error={configQuery.error} />;
  }
  if (configQuery.isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const config = configQuery.data as CopilotPromptConfigurationsDto;

  return (
    <CopilotConfigurationsForm
      defaultValues={{ configurations: config.configurations }}
      isSuperAdmin={sessionUser.isSuperAdmin}
      isSubmitting={mutation.isPending}
      submitLabel={t("page.portfolio-integrations.project-setup.save-next")}
      onSubmit={(data) => mutation.mutate(data, { onSuccess: onSaved })}
    />
  );
}

function RemsProjectSetup({ projectId, onSaved }: { projectId: string; onSaved: () => void }): React.ReactNode {
  const { t } = useTranslation();
  const integrationQueries = useIntegrationQueries();
  const buildingQueries = useBuildingQueries();
  const configQuery = useQuery(integrationQueries.remsConfiguration(projectId));
  const buildingsQuery = useQuery(buildingQueries.list({ projectId }));
  const mutation = integrationMutations.useEditRemsConfiguration(projectId);

  if (configQuery.error || buildingsQuery.error) {
    return <ErrorPage error={configQuery.error ?? buildingsQuery.error} />;
  }
  if (configQuery.isLoading || buildingsQuery.isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const config = configQuery.data as RemsProjectConfigurationDto;

  return (
    <RemsForm
      buildings={buildingsQuery.data?.items ?? []}
      configureWhileDisabled
      defaultValues={{
        tickets: config.tickets,
        contracts: config.contracts,
        communityUserContactId: config.communityUserContactId ?? "",
        communityUserRelationId: config.communityUserRelationId ?? "",
        complexCode: config.complexCode ?? "",
        tenantId: config.tenantId ?? "",
        buildingComplexCodes: config.buildingComplexCodes ?? [],
      }}
      isSubmitting={mutation.isPending}
      submitLabel={t("page.portfolio-integrations.project-setup.save-next")}
      onSubmit={(data) => mutation.mutate(data, { onSuccess: onSaved })}
    />
  );
}

function ILoqProjectSetup({ projectId, onSaved }: { projectId: string; onSaved: () => void }): React.ReactNode {
  const { t } = useTranslation();
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();
  const configQuery = useQuery(integrationQueries.iloqConfiguration(projectId));
  const usersQuery = useQuery(userQueries.listAdmins(projectId));

  if (configQuery.error || usersQuery.error) {
    return <ErrorPage error={configQuery.error ?? usersQuery.error} />;
  }
  if (configQuery.isLoading || usersQuery.isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const config = configQuery.data as ILoqProjectConfigurationDto;
  const users = usersQuery.data ?? [];

  return (
    <ILoqForm
      projectId={projectId}
      defaultValues={{
        serviceAddress: config.serviceAddress ?? "",
        customerCode: config.customerCode ?? "",
        username: config.username ?? "",
        password: "",
        lockGroup: null,
        realEstate: null,
        zone: null,
        supportUser: users.find((user) => user.id === config.supportUserId) ?? null,
        mappings: Object.fromEntries(
          (config.securityAccessMappings ?? []).map((mapping) => [mapping.addressId, mapping.securityAccessIds]),
        ),
      }}
      adminUsers={users}
      hasSavedConfig={Boolean(config.serviceAddress)}
      savedLockGroupId={config.lockGroupId}
      savedRealEstateId={config.realEstateId}
      savedZoneId={config.zoneId}
      enabledOnSave={config.enabled}
      submitLabel={t("page.portfolio-integrations.project-setup.save-next")}
      onSaved={onSaved}
    />
  );
}
